import pdfMakeImport from 'pdfmake/build/pdfmake';
import pdfFontsImport from 'pdfmake/build/vfs_fonts';
import type { Column, Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import type { ReportSection } from '../screens/user/report.screen';

// pdfmake ships an UMD bundle; the default export shape can vary across bundler interop paths,
// so unwrap defensively and expose the vfs both ways for maximum compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pdfMake: any = (pdfMakeImport as any).default ?? pdfMakeImport;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pdfFonts: any = (pdfFontsImport as any).default ?? pdfFontsImport;

pdfMake.vfs = pdfFonts;
if (typeof pdfMake.addVirtualFileSystem === 'function') {
  pdfMake.addVirtualFileSystem(pdfFonts);
}

// pdfmake 0.3.x requires an explicit fonts dictionary when vfs is set manually
pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
};

interface ReportLabels {
  reportTitle: string;
  target: string;
  outcome: string;
  score: string;
  progress: string;
  average: string;
  period: string;
  noRecords: string;
}

export type ReportAction = 'share' | 'open';

interface GenerateReportPdfArgs {
  userName: string;
  timeFrame: string;
  periodRange: string;
  generatedAt: string;
  language: string;
  labels: ReportLabels;
  sections: ReportSection[];
  action: ReportAction;
}

export async function generateReportPdf(args: GenerateReportPdfArgs): Promise<void> {
  const { userName, timeFrame, periodRange, generatedAt, labels, sections, action } = args;

  const logo = await loadLogo();

  const headerColumns: Content = {
    columns: [
      ...(logo ? [{ image: logo, width: 48, margin: [0, 6, 0, 0] } as Column] : []),
      {
        stack: [
          {
            columns: [
              { text: `${labels.reportTitle} — ${userName}`, style: 'title' },
              { text: generatedAt, style: 'date', alignment: 'right', margin: [0, 4, 0, 0] },
            ],
          },
          { text: periodRange, style: 'period', margin: [0, 4, 0, 0] },
        ],
        margin: [0, 8, 0, 0],
      },
    ],
    columnGap: 20,
    margin: [0, 0, 0, 20],
  };

  const sectionContents = sections.flatMap((section, i) => {
    const items = buildSectionContent(section, labels);
    if (i > 0) items.unshift(divider());
    return items;
  });

  const content: Content = [headerColumns, ...sectionContents];

  const docDefinition: TDocumentDefinitions = {
    content,
    pageMargins: [40, 40, 40, 40],
    defaultStyle: { font: 'Roboto', fontSize: 10, color: '#222' },
    styles: {
      title: { fontSize: 18, bold: true, color: '#5a4a3a' },
      date: { fontSize: 10, color: '#666' },
      sectionTitle: { fontSize: 14, bold: true, margin: [0, 0, 0, 2] },
      notes: { fontSize: 9, color: '#555', italics: true, margin: [0, 0, 0, 6] },
      period: { fontSize: 10, color: '#666' },
      statLabel: { color: '#666' },
      statValue: { bold: true, alignment: 'right' },
      empty: { italics: true, color: '#888' },
    },
  };

  const filename = `virtus-report-${timeFrame.toLowerCase()}-${todayStr()}.pdf`;
  const pdf = pdfMake.createPdf(docDefinition);
  const blob: Blob = await pdf.getBlob();

  if (action === 'share') {
    const file = new File([blob], filename, { type: 'application/pdf' });
    if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          text: `${labels.reportTitle} — ${userName} (${periodRange})`,
        });
        return;
      } catch {
        // user cancelled or share failed; fall through to download
      }
    }
    downloadBlob(blob, filename);
    return;
  }

  // action === 'open'
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');
  if (!opened) {
    // popup blocked (common in PWA standalone); fall back to download
    downloadBlob(blob, filename);
  } else {
    // release the object URL after the tab loaded it
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }
}

function buildSectionContent(section: ReportSection, labels: ReportLabels): Content[] {
  const content: Content[] = [];

  // keep each habit's block on a single page when it fits
  const block: Content[] = [{ text: section.title, style: 'sectionTitle' }];

  if (section.notes) {
    block.push({ text: section.notes, style: 'notes' });
  }

  if (!section.hasRecords) {
    block.push({ text: labels.noRecords, style: 'empty' });
    content.push({ stack: block, margin: [0, 0, 0, 16], unbreakable: true });
    return content;
  }

  if (section.chartImage) {
    block.push({ image: section.chartImage, width: 500, margin: [0, 0, 0, 6] });
  }

  if (section.averageText) {
    block.push({
      columns: [
        { text: labels.average, style: 'statLabel' },
        { text: section.averageText, style: 'statValue' },
      ],
      margin: [0, 4, 0, 0],
    });
  } else {
    block.push({
      table: {
        widths: ['*', 'auto'],
        body: [
          [
            { text: labels.target, style: 'statLabel', border: [false, false, false, false] },
            { text: section.targetLabel ?? '', style: 'statValue', border: [false, false, false, false] },
          ],
          [
            { text: labels.outcome, style: 'statLabel', border: [false, false, false, false] },
            { text: section.outcomeLabel ?? '', style: 'statValue', border: [false, false, false, false] },
          ],
          [
            {
              text: section.scoreLabel ?? labels.score,
              style: 'statLabel',
              bold: true,
              border: [false, true, false, false],
            },
            {
              text: section.scoreValue ?? '',
              style: 'statValue',
              border: [false, true, false, false],
            },
          ],
        ],
      },
      layout: 'noBorders',
    });
  }

  content.push({ stack: block, margin: [0, 0, 0, 20], unbreakable: true });
  return content;
}

function divider(): Content {
  return {
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#d0c9be' }],
    margin: [0, 0, 0, 16],
  };
}

async function loadLogo(): Promise<string | undefined> {
  try {
    const res = await fetch('/apple-touch-icon.png');
    if (!res.ok) return;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
